import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  ActionsBanner,
  BillableIcon,
  DateTime,
  DeleteConfirmation,
  Duration,
  ExportDialog,
  FiltersBar,
  Icon,
  IconButton,
  ListView,
  ListViewActions,
  ListViewMenu,
  ListViewSelection,
  Page,
  PeriodFilter,
  TimeApprovalPopover,
  TimeLockIcon,
  Tooltip,
  YesNoFilter,
  AccountingIntegrationIcon,
} from '~/components';
import FixedDropdown from '~/components/FixedDropdown';
import { BulkActionMenu } from '~/components/bulk-action';
import {
  ClientFiltersBar,
  ClientFiltersGroup,
  FilterButton,
  FiltersDrawer,
  MemberFiltersBar,
  MemberFiltersGroup,
  ProjectFiltersBar,
  ProjectFiltersGroup,
  ProjectRoleFiltersGroup,
  TaskFiltersBar,
  TaskFiltersGroup,
  TimeFiltersBar,
  TimeFiltersGroup,
} from '~/components/filters';
import { useApi, useConfirmation, useIntegrations, useSubscription, useWorkspace } from '~/contexts';
import {
  useAuth,
  useClientFilters,
  useDocumentTitle,
  useIsMounted,
  useMemberFilters,
  useProjectFilters,
  useProjectRoleFilters,
  useRowSelection,
  useSearchParams,
  useSearchParamsConfig,
  useTaskFilters,
  useTimeFilters,
} from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { QuerySort, intervalOptions, intervalsByScope, mimeTypes } from '~/utils';
import useReportsSearchParamsConfig from '../../reports/hooks/useReportsSearchParamsConfig';
import TimeAccountingIndicator from '../TimeAccountingIndicator';
import EditTimeEntry from '../edit-time-entry';
import ViewTimeEntry from '../view-time-entry';
import BulkDelete from './bulk/BulkDelete';
import BulkSetApprovalStatus from './bulk/BulkSetApprovalStatus';

const S = {
  Time: styled.div`
    position: relative;
    padding: 0.2rem 1.5rem;
    font-weight: ${weights.medium};
    border-radius: 999rem;
    width: 6.25rem;
    text-align: center;

    ${({ status }) =>
      ({
        not_submitted: css`
          background: ${colors.grey10};
          color: ${colors.black};
        `,
        pending_approval: css`
          background: ${colors.warning10};
          color: ${colors.warning};
        `,
        rejected: css`
          background: ${colors.danger10};
          color: ${colors.danger};
        `,
        approved: css`
          background: ${colors.primary10};
          color: ${colors.primary};
        `,
      })[status]}
  `,

  Stopwatch: styled(Tooltip)`
    position: absolute;
    top: 0.2rem;
    left: 0.5rem;
  `,

  TimeEntryInfo: styled.div`
    flex: 1;
    display: flex;
    justify-content: flex-end;
    align-items: center;

    > div:first-child {
      margin-right: 1rem;
    }
  `,

  Checkbox: styled.div`
    > label > div {
      background: ${colors.white};
    }
  `,

  ProjectInfo: styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
    width: 100%;
  `,

  Notes: styled.p`
    width: 100%;
    font-size: 0.75rem;
    text-align: justify;
    color: ${colors.grey55};

    &:before {
      content: open-quote;
    }

    &:after {
      content: close-quote;
    }
  `,
};

function TimeAuditingPage() {
  const documentTitle = useDocumentTitle('Time Auditing');

  const { workspace } = useWorkspace();
  const [drawer, setDrawer] = useState(null);
  const api = useApi();
  const { notify } = useSubscription();
  const confirmation = useConfirmation();
  const auth = useAuth();
  const integrations = useIntegrations();

  const isMounted = useIsMounted();

  const memberFilters = useMemberFilters();
  const clientFilters = useClientFilters();
  const projectFilters = useProjectFilters();
  const roleFilters = useProjectRoleFilters();
  const taskFilters = useTaskFilters();
  const timeFilters = useTimeFilters();

  const [query, setQuery] = useState({
    isReady: false,
    searchParamsStatus: 'pending',
    data: null,
    params: {
      period: null,
      sort: new QuerySort('date', 'desc'),
      page: 0,
      size: 100,
      includeLockedItems: 'yes',
      showNotes: 'no',
      imported: null,
      excludedFromBilling: null,
      ...memberFilters.filters,
      ...clientFilters.filters,
      ...projectFilters.filters,
      ...roleFilters.filters,
      ...taskFilters.filters,
      ...timeFilters.filters,
    },
    action: 'load',
  });

  const setParams = (params) => {
    setQuery((state) => ({
      ...state,
      action: 'filter',
      params: { ...state.params, ...params, page: 0 },
      searchParamsStatus: 'ready',
    }));
  };

  const loadMore = () => {
    setQuery((state) => {
      if (
        state.searchParamsStatus !== 'ready' ||
        state.action !== null ||
        !state.data ||
        state.data.total <= state.data.results.length
      )
        return state;

      return {
        ...state,
        params: { ...state.params, page: state.params.page + 1 },
        action: 'load-more',
      };
    });
  };

  const { action, data, isReady, params, searchParamsStatus } = query;

  const selection = useRowSelection({ rows: data?.results });

  const searchParamsConfig = useSearchParamsConfig();
  const reportsSearchParamsConfig = useReportsSearchParamsConfig();
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        period: {
          ...reportsSearchParamsConfig.period,
          default: intervalOptions.past_30_days,
        },
        sort: { default: new QuerySort('date', 'desc'), ...searchParamsConfig.sort },
        includeLockedItems: { default: 'yes', valid: ['yes', 'no'] },
        showNotes: { default: 'no', valid: ['yes', 'no'] },
        imported: { valid: ['yes', 'no'] },
        excludedFromBilling: { valid: ['yes', 'no'] },
        ...memberFilters.searchParamsConfig,
        ...clientFilters.searchParamsConfig,
        ...projectFilters.searchParamsConfig,
        ...roleFilters.searchParamsConfig,
        ...taskFilters.searchParamsConfig,
        ...timeFilters.searchParamsConfig,
      }),
      [
        searchParamsConfig,
        reportsSearchParamsConfig,
        clientFilters,
        projectFilters,
        memberFilters,
        roleFilters,
        taskFilters,
        timeFilters,
      ],
    ),
    sessionKey: 'time_auditing',
    onChange: useCallback((params) => setParams((state) => ({ ...state, ...params })), []),
  });

  useEffect(() => {
    if (searchParamsStatus !== 'pending') return;
    searchParams.get().then((params) => {
      if (params) setParams(params);
    });
  }, [searchParams, searchParamsStatus]);

  const urlSearchParams = useMemo(
    () => ({
      start: params.period?.start ?? undefined,
      end: params.period?.end ?? undefined,
      sort: params.sort,
      includeLockedItems: params.includeLockedItems ?? undefined,
      showNotes: params.showNotes ?? undefined,
      imported: params.imported ?? undefined,
      excludeFromBilling: params.excludeFromBilling ?? undefined,
      ...memberFilters.mapUrlSearchParams(params),
      ...clientFilters.mapUrlSearchParams(params),
      ...projectFilters.mapUrlSearchParams(params),
      ...taskFilters.mapUrlSearchParams(params),
      ...timeFilters.mapUrlSearchParams(params),
    }),
    [params, memberFilters, clientFilters, projectFilters, taskFilters, timeFilters],
  );

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www
        .workspaces(workspace.id)
        .timeAdmin()
        .getAuditing({ ...urlSearchParams, page: params.page, size: params.size });

      if (!isMounted.current) return;

      setQuery((state) => ({
        ...state,
        action: null,
        searchParamsStatus: 'ready',
        data: {
          ...data,
          results: state.action === 'load-more' ? [...state.data.results, ...data.results] : data.results,
          total: data.total,
        },
      }));
    } catch (error) {
      setQuery((state) => ({ ...state, data: { total: 0, results: [] } }));
    }
  }, [workspace.id, api, isMounted, urlSearchParams, params.page, params.size]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const handleFilter = (values) => {
    if (values !== params) {
      setQuery((state) => ({ ...state, status: 'filtering' }));
      selection.clear();
    }

    setParams({ ...values });
    searchParams.set({ ..._.omit(values, 'sort') });
    hideFilters();
  };

  const removeItem = (id) => {
    setQuery((state) => ({
      ...state,
      data: {
        ...state.data,
        results: state.data?.results.filter((i) => i.id !== id),
        total: state.data.total - 1,
      },
    }));
  };

  const updateItems = (items) => {
    setQuery((state) => ({
      ...state,
      data: {
        ...state.data,
        results: state.data?.results.map((i) => {
          const updatedItem = items.find((item) => item.id === i.id);
          return updatedItem ? { ...i, ...updatedItem } : i;
        }),
      },
    }));
  };

  const updateItem = (item) => {
    setQuery((state) => ({
      ...state,
      data: {
        ...state.data,
        results: state.data?.results.map((i) => (i.id === item.id ? { ...i, ...item } : i)),
      },
    }));
  };

  const handleResultClick = (entry, mode) => {
    setDrawer({ entry, mode });
  };

  const handleCloseDrawer = () => {
    setDrawer(null);
    documentTitle.set('Time Auditing');
  };

  async function handleDeleted(entry) {
    await api.www.workspaces(workspace.id).timeAdmin(entry.id).delete();
    removeItem(entry.id);
  }

  const handleTimerChange = (entry) => {
    // Stop all timers for the member
    const entries = data.results
      .filter((e) => !!e.timerStartedAt && e.member.id === entry.member.id)
      .map((e) => ({
        ...e,
        minutes: moment.duration(e.minutes, 'minutes').add(moment().diff(e.timerStartedAt)).asMinutes(),
        timerStartedAt: null,
      }));

    updateItems([...entries, entry]);
    notify(useSubscription.keys_refresh_timer);
  };

  const handleBulkDelete = async () => {
    await confirmation.prompt((resolve) => (
      <BulkDelete
        ids={selection.ids}
        onComplete={async () => {
          selection.clear();
          if (params.page !== 0) {
            await fetchData();
          } else {
            setParams({ page: 0 });
          }
        }}
        onClose={resolve}
      />
    ));
  };

  const handleBulkUpdateApprovalStatus = async () => {
    await confirmation.prompt((resolve) => (
      <BulkSetApprovalStatus
        ids={selection.ids}
        onComplete={async () => {
          selection.clear();
          if (params.page !== 0) {
            await fetchData();
          } else {
            setParams({ page: 0 });
          }
        }}
        onClose={resolve}
      />
    ));
  };

  const handleSort = ({ column, sort }) => {
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    const querySort = new QuerySort(column, direction);
    setParams({ sort: querySort });
    searchParams.set({ sort: querySort });
    selection.clear();
  };

  const handleExport = async (filename, mimeType) => {
    await confirmation.prompt((resolve) => (
      <ExportDialog
        filename={filename}
        onLoad={api.www
          .workspaces(workspace.id)
          .timeAdmin()
          .exportAuditing(
            { ...urlSearchParams, size: null },
            {
              headers: { accept: mimeType },
              responseType: 'blob',
            },
          )}
        onClose={resolve}
      />
    ));
  };

  const handleEntryDeleted = (entry) => {
    removeItem(entry.id);
    notify(useSubscription.keys.refresh_timer);
  };

  const handleEntrySaved = (entry) => {
    updateItem(entry);
    notify(useSubscription.keys.refresh_timer);
  };

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);

  if (!isReady && !data) return <PageLoader />;

  return (
    <>
      <Page>
        <Page.Header>
          <Page.Info>
            <Page.Eyebrow>Time</Page.Eyebrow>
            <Page.Title>Time Auditing</Page.Title>
          </Page.Info>

          <Page.Actions>
            <FixedDropdown align="right">
              {({ setIsOpen }) => (
                <>
                  <FixedDropdown.Trigger>
                    <IconButton tooltip="Export" icon="download" onClick={() => setIsOpen(true)} />
                  </FixedDropdown.Trigger>
                  <FixedDropdown.Menu>
                    <FixedDropdown.Item
                      onClick={async () => {
                        await handleExport(`time_auditing.csv`, mimeTypes.csv);
                        setIsOpen(false);
                      }}>
                      Export to CSV
                    </FixedDropdown.Item>

                    <FixedDropdown.Item
                      onClick={async () => {
                        await handleExport(`time_auditing.xlsx`, mimeTypes.xlsx);
                        setIsOpen(false);
                      }}>
                      Export to Excel
                    </FixedDropdown.Item>
                  </FixedDropdown.Menu>
                </>
              )}
            </FixedDropdown>

            <FilterButton isOutline onClick={showFilters} />
          </Page.Actions>
        </Page.Header>

        <Page.Filters>
          <FiltersBar>
            <PeriodFilter
              name="period"
              placeholder="Date Range"
              value={params.period}
              intervals={[intervalOptions.all_dates, ...intervalsByScope.day]}
              onChange={({ value }) => handleFilter({ period: value })}
            />

            {!_.isEmpty(params.includeLockedItems) && (
              <LockStatusFilter
                value={params.includeLockedItems}
                onChange={({ value }) => handleFilter({ includeLockedItems: value })}
              />
            )}

            {!_.isEmpty(params.imported) && (
              <ImportedFilter value={params.imported} onChange={({ value }) => handleFilter({ imported: value })} />
            )}

            {!_.isEmpty(params.excludeFromBilling) && (
              <ExcludeFromBillingFilter
                value={params.excludeFromBilling}
                onChange={({ value }) => handleFilter({ excludeFromBilling: value })}
              />
            )}

            <ShowNotesFilter value={params.showNotes} onChange={({ value }) => handleFilter({ showNotes: value })} />

            <ClientFiltersBar filters={params} onChange={handleFilter} />

            <ProjectFiltersBar filters={params} sticky={['projects']} onChange={handleFilter} />

            <TimeFiltersBar
              filters={params}
              sticky={['timeBillableTypes', 'timeApprovalStatuses']}
              onChange={handleFilter}
            />

            <TaskFiltersBar filters={params} onChange={handleFilter} />

            <MemberFiltersBar
              filters={params}
              sticky={['members', 'memberBusinessUnits', 'memberPractices']}
              onChange={handleFilter}
            />
          </FiltersBar>
        </Page.Filters>

        <Page.ListView>
          <ListView>
            <ListView.Header sticky>
              <ListViewSelection.Column
                selection={selection}
                disabled={data.results.length === 0 || action === 'filter'}
              />
              <ListView.Column width="3.5rem" align="center">
                <TimeLockIcon />
              </ListView.Column>
              <ListView.Column width="3rem" isVisible={auth.workspace.manage && integrations.accounting}>
                <AccountingIntegrationIcon />
              </ListView.Column>
              <ListView.Column name="date" width="7.5rem" sort={params.sort} onSort={handleSort}>
                Date
              </ListView.Column>
              <ListView.Column minWidth="16rem" name="member" sort={params.sort} onSort={handleSort}>
                Member
              </ListView.Column>
              <ListView.Column minWidth="16rem" name="project" align="left" sort={params.sort} onSort={handleSort}>
                Project/Client
              </ListView.Column>
              <ListView.Column minWidth="16rem">Role/Task</ListView.Column>
              <ListView.Column align="center" width="11rem">
                Time
              </ListView.Column>
              <ListViewActions.Column />
            </ListView.Header>
            <ListView.Body fade={action === 'filter'}>
              {data.results.map((entry) => {
                const locked = entry.isLocked;
                const tooltip = locked ? 'The time entry is locked.' : undefined;

                const handleEdit = () => handleResultClick(entry, 'edit');
                const handleView = () => handleResultClick(entry, 'view');

                const handleDelete = async () => {
                  const confirm = await confirmation.prompt((resolve) => (
                    <DeleteConfirmation resolve={resolve} title="Delete Time Entry">
                      Are you sure you want to delete this time entry?
                    </DeleteConfirmation>
                  ));
                  if (!confirm) return;

                  handleDeleted(entry);
                };

                return (
                  <ListView.Row key={entry.id} onClick={locked ? handleView : handleEdit}>
                    <ListViewSelection.Cell id={entry.id} selection={selection} />
                    <ListView.Cell>{locked && <TimeLockIcon value={entry.lockStatusId} />}</ListView.Cell>
                    <ListView.Cell>
                      <TimeAccountingIndicator
                        accountingProvider={entry.invoice?.accountingProvider}
                        accountingConnectionName={entry.invoice?.accountingConnectionName}
                        invoiceNumber={entry.invoice?.number}
                        isSynced={entry.invoice?.accountingSynced}
                      />
                    </ListView.Cell>
                    <ListView.Cell>
                      <DateTime value={entry.date} />
                    </ListView.Cell>
                    <ListView.Cell>{entry.member.name}</ListView.Cell>
                    <ListView.Cell>
                      <S.ProjectInfo>
                        {entry.typeId === 'project_time' ? (
                          entry.project && (
                            <ListView.CellContent>
                              {entry.project.name}
                              <small>{entry.project.client.name}</small>
                            </ListView.CellContent>
                          )
                        ) : (
                          <ListView.CellContent>{entry.type.name}</ListView.CellContent>
                        )}

                        {params.showNotes === 'yes' && !!entry.notes && (
                          <S.Notes>
                            <em>{entry.notes}</em>
                          </S.Notes>
                        )}
                      </S.ProjectInfo>
                    </ListView.Cell>
                    <ListView.Cell>
                      {entry.typeId === 'project_time' ? (
                        entry.project && (
                          <ListView.CellContent>
                            {entry.project?.useRoles && entry.role?.name}
                            {entry.task && <small>{entry.task.name}</small>}
                          </ListView.CellContent>
                        )
                      ) : (
                        <ListView.CellContent>{entry.timeOffType?.name}</ListView.CellContent>
                      )}
                    </ListView.Cell>
                    <ListView.Cell flex="1">
                      <TimeApprovalPopover timeEntryId={entry.id}>
                        <S.TimeEntryInfo>
                          <BillableIcon value={entry.isActuallyBillable} />
                          <S.Time status={entry.statusId}>
                            {entry.timerStartedAt && (
                              <S.Stopwatch message="This time entry has a running timer">
                                <Icon icon="stopwatch" type="far" />
                              </S.Stopwatch>
                            )}
                            <Duration minutes={entry.minutes} timerStartedAt={entry.timerStartedAt} trim />
                          </S.Time>
                        </S.TimeEntryInfo>
                      </TimeApprovalPopover>
                    </ListView.Cell>
                    <ListViewActions>
                      {locked ? (
                        <ListViewActions.View onClick={handleView} />
                      ) : (
                        <ListViewActions.Edit onClick={handleEdit} />
                      )}

                      <hr />

                      <ListViewMenu>
                        {({ setIsOpen }) => {
                          return (
                            <>
                              <ListViewMenu.Item onClick={handleView}>View</ListViewMenu.Item>

                              <ListViewMenu.Item disabled={locked} tooltip={tooltip} onClick={handleEdit}>
                                Edit
                              </ListViewMenu.Item>

                              <ListViewMenu.Item
                                disabled={locked}
                                tooltip={tooltip}
                                onClick={() => setIsOpen(false) || handleDelete()}>
                                Delete
                              </ListViewMenu.Item>
                            </>
                          );
                        }}
                      </ListViewMenu>
                    </ListViewActions>
                  </ListView.Row>
                );
              })}

              {data.results.length === 0 && <ListView.Empty />}

              {data.total > data.results.length && (
                <ListView.Loader key={data.results.length} onIntersecting={loadMore} />
              )}
            </ListView.Body>

            <ListView.Status total={data.total} label="Time Entry" isLoading={!!action} />
          </ListView>
        </Page.ListView>

        {selection.any > 0 && (
          <ActionsBanner>
            <BulkActionMenu count={selection.count}>
              {auth.time.approve && (
                <BulkActionMenu.Item onClick={() => handleBulkUpdateApprovalStatus()}>
                  Set approval status
                </BulkActionMenu.Item>
              )}
              <BulkActionMenu.Item onClick={() => handleBulkDelete()}>Delete</BulkActionMenu.Item>
            </BulkActionMenu>
          </ActionsBanner>
        )}
      </Page>

      <Filters values={params} isOpen={filtersVisible} onApply={handleFilter} onClose={hideFilters} />

      {drawer &&
        {
          edit: () => (
            <EditTimeEntry
              id={drawer.entry.id}
              memberId={drawer.entry.member.id}
              onSubmit={(body) => api.www.workspaces(workspace.id).timeAdmin(drawer.entry.id).update(body)}
              onConfirmDelete={(id) => api.www.workspaces(workspace.id).timeAdmin(id).delete()}
              onSaved={handleEntrySaved}
              onDeleted={handleEntryDeleted}
              onClose={handleCloseDrawer}
              onTimerChange={handleTimerChange}
            />
          ),
          view: () => <ViewTimeEntry id={drawer.entry.id} onClose={handleCloseDrawer} />,
        }[drawer.mode]()}
    </>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const auth = useAuth();

  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <PeriodFilter
        name="period"
        placeholder="Date Range"
        value={filters.period}
        intervals={[intervalOptions.all_dates, ...intervalsByScope.day]}
        onChange={({ value }) => handleFilter({ period: value })}
      />

      <LockStatusFilter
        value={filters.includeLockedItems}
        onChange={({ value }) => handleFilter({ includeLockedItems: value })}
      />

      {auth.workspace.manage && (
        <ImportedFilter value={filters.imported} onChange={({ value }) => handleFilter({ imported: value })} />
      )}

      {auth.workspace.manage && (
        <ExcludeFromBillingFilter
          value={filters.excludeFromBilling}
          onChange={({ value }) => handleFilter({ excludeFromBilling: value })}
        />
      )}

      <ShowNotesFilter value={filters.showNotes} onChange={({ value }) => handleFilter({ showNotes: value })} />

      <ClientFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectRoleFiltersGroup filters={filters} onChange={handleFilter} />

      <TaskFiltersGroup filters={filters} onChange={handleFilter} />

      <MemberFiltersGroup filters={filters} onChange={handleFilter} />

      <TimeFiltersGroup filters={filters} onChange={handleFilter} />
    </FiltersDrawer>
  );
}

function LockStatusFilter({ value, onChange }) {
  return <YesNoFilter name="includeLockedItems" placeholder="Include Locked Items" value={value} onChange={onChange} />;
}

function ImportedFilter({ value, onChange }) {
  return <YesNoFilter name="imported" placeholder="Imported" value={value} clearable onChange={onChange} />;
}

function ExcludeFromBillingFilter({ value, onChange }) {
  return (
    <YesNoFilter
      name="excludeFromBilling"
      placeholder="Exclude from Billing"
      value={value}
      clearable
      onChange={onChange}
    />
  );
}

function ShowNotesFilter({ value, onChange }) {
  return <YesNoFilter icon="gear" name="showNotes" placeholder="Show Notes" value={value} onChange={onChange} />;
}

export default TimeAuditingPage;
